import { useEffect, useRef, useState } from 'react';

import { request } from '../lib/api';
import { copyToClipboard, type TapPageSharing } from '../pages/portalTypes';

type Props = { slugId: string; sharing: TapPageSharing };

const unavailableMessages = {
  inactive: 'This page is inactive. Restore access before sharing.',
  unpublished: 'Publish your page to enable sharing.',
  unavailable: 'Sharing is unavailable until the published page is ready.'
};

function ShareControls({ slugId, sharing }: Props) {
  const [revealed, setRevealed] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; failed: boolean } | null>(null);
  const exposureVersion = useRef(0);
  const qrRequest = useRef<AbortController | null>(null);
  useEffect(() => () => qrRequest.current?.abort(), []);

  if (sharing.state !== 'ready' || !sharing.href || !sharing.kind) {
    return <div className="tap-share-note"><h3>Share your page</h3><p>{unavailableMessages[sharing.state === 'ready' ? 'unavailable' : sharing.state]}</p></div>;
  }

  const href = sharing.href;
  const isPrivate = sharing.kind === 'private';
  const visible = !isPrivate || revealed;

  const hide = () => {
    exposureVersion.current++;
    qrRequest.current?.abort();
    qrRequest.current = null;
    setRevealed(false);
    setQr(null);
    setLoadingQr(false);
    setFeedback(null);
  };
  const copy = async () => {
    const version = exposureVersion.current;
    setFeedback(null);
    try {
      await copyToClipboard(href);
      if (version !== exposureVersion.current) return;
      setFeedback({ text: 'Page link copied.', failed: false });
    } catch {
      if (version !== exposureVersion.current) return;
      setFeedback({ text: 'Could not copy the link. Select the link and copy it manually.', failed: true });
    }
  };
  const showQr = async () => {
    qrRequest.current?.abort();
    const controller = new AbortController();
    qrRequest.current = controller;
    setQr(null);
    setLoadingQr(true);
    setFeedback(null);
    try {
      const result = await request<{ href: string; svg: string }>(`/me/tap-page/${encodeURIComponent(slugId)}/qr`, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (result.href !== href) throw new Error('Page sharing changed');
      setQr(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(result.svg)}`);
    } catch {
      if (!controller.signal.aborted) setFeedback({ text: 'Could not create the QR code. Refresh your page status and try again.', failed: true });
    } finally {
      if (!controller.signal.aborted) setLoadingQr(false);
    }
  };
  const share = async () => {
    const version = exposureVersion.current;
    setFeedback(null);
    try {
      await navigator.share({ title: 'My InkTap page', url: href });
    } catch (error) {
      if (version !== exposureVersion.current) return;
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setFeedback({ text: 'Could not share the link. You can copy it instead.', failed: true });
      }
    }
  };

  return (
    <section className="tap-share-note" aria-label="Page sharing">
      <h3>Share your page</h3>
      <p>{isPrivate
        ? 'Anyone with this private link or its QR can open the page. Reveal it when you intend to share.'
        : 'Use your public link in messages, email, and social profiles.'}</p>
      {isPrivate ? (
        <button type="button" className="button-secondary tap-share-reveal" aria-expanded={revealed} onClick={revealed ? hide : () => setRevealed(true)}>
          {revealed ? 'Hide private link' : 'Reveal private link'}
        </button>
      ) : null}
      {visible ? (
        <div className="tap-share-tools">
          <label className="tap-share-link">
            <span>Page share link</span>
            <input value={href} readOnly onFocus={event => event.currentTarget.select()} />
          </label>
          <div className="tap-share-tools__actions">
            <a className="button-secondary" href={href} target="_blank" rel="noopener noreferrer">Open live page</a>
            <button type="button" className="button-secondary" onClick={() => void copy()}>Copy link</button>
            <button type="button" className="button-secondary" disabled={loadingQr} onClick={() => void showQr()}>{loadingQr ? 'Creating QR...' : 'Show QR'}</button>
            {typeof navigator.share === 'function' ? <button type="button" className="button-secondary" onClick={() => void share()}>Share link</button> : null}
          </div>
          {loadingQr ? <p role="status">Creating your page QR code...</p> : null}
          {qr ? (
            <div className="tap-share-qr">
              <img src={qr} width="240" height="240" alt="QR code for your tap page" />
              <div>
                <p>Share this code on screen or download it for your page.</p>
                <a className="button-secondary" href={qr} download="tap-page-qr.svg">Download QR</a>
              </div>
            </div>
          ) : null}
          {feedback ? <p role={feedback.failed ? 'alert' : 'status'}>{feedback.text}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

export function TapPageSharePanel(props: Props) {
  // A change in page, availability or alias removes prior private-link/QR exposure.
  return <ShareControls key={`${props.slugId}:${props.sharing.state}:${props.sharing.kind}:${props.sharing.href}`} {...props} />;
}
